import { Activity, ActivityContent, ActivityType } from '@/features/course/admin/types/activity';
import { ActivityId, BlockId } from '@/administration/pages/Course/store/types';
import { ViewQuizPage } from '@/administration/pages/Course/pages/quiz/View/ViewQuizPage/ViewQuizPage';
import { ViewTextPage } from '@/administration/pages/Course/pages/text/index';
import { ViewReflectionPage } from '@/administration/pages/Course/pages/reflection/variants/View/index';
import { BlockType } from '@/administration/pages/Course/store/block/types';
import { ViewResultPage } from '@/administration/pages/Course/pages/result/View/index';
import { CourseAssignmentActivity } from '@/features/course/learner/organisms/CourseAssignmentActivity';
import { CustomEditorV2Readonly } from '@/component/customEditorV2';

interface ViewActivityContentV2Props {
  activity: Activity;
  onActivityChange: (id: ActivityId, diff: Partial<Activity>) => void;
  spaceId: number;
}

export const ViewActivityContentV2 = ({
  activity,
  onActivityChange,
  spaceId,
}: ViewActivityContentV2Props): JSX.Element | null => {
  if (activity.type === ActivityType.Page) {
    const richTextActivityContent = activity.content as ActivityContent;
    return (
      <ViewTextPage
        id={activity.id}
        spaceId={spaceId}
        title={activity.content.title}
        text={richTextActivityContent.text || '[]'}
        onCompleted={() => onActivityChange(activity.id, { completed: true })}
      />
    );
  } else if (activity.type === ActivityType.Quiz) {
    return (
      <ViewQuizPage
        id={activity.id}
        text={activity?.content?.text as string}
        spaceId={spaceId}
        remoteId={activity.remoteId || 0}
        onCompleted={() => onActivityChange(activity.id, { completed: true })}
        passingCriteria={activity.content.passingCriteria}
        totalQuestionsCount={activity.content.questions?.length ?? 0}
      />
    );
  } else if (activity.type === ActivityType.Reflection) {
    const reflectionActivityContent = activity.content as ActivityContent;
    return (
      <ViewReflectionPage
        pageId={activity.remoteId || 0}
        spaceId={spaceId}
        title={reflectionActivityContent.title}
        description={reflectionActivityContent.description as string}
        postId={reflectionActivityContent.postId}
        sectionPath={reflectionActivityContent.sectionPath}
        onCompleted={(completed) => onActivityChange(activity.id, { completed })}
      />
    );
  } else if (activity.type === ActivityType.Assignment) {
    return (
      <CourseAssignmentActivity
        key={activity.id}
        learningActivityId={activity.remoteId || 0}
        onCompleted={() => onActivityChange(activity.id, { completed: true })}
        instructions={
          <CustomEditorV2Readonly
            value={activity.content.text || '[]'}
            style={{ fontSize: 14 }}
            toggledFeatures={{ trimEndEmptyParagraphs: true }}
            $fullSize
            minimized
            spaceId={spaceId}
          />
        }
      />
    );
  } else if (activity.type === ActivityType.Result) {
    const resultActivityContent = activity.content as ActivityContent;
    return (
      <ViewResultPage
        id={activity.id}
        block={{
          id: activity.id as unknown as BlockId,
          type: BlockType.RESULT,
          level: resultActivityContent.level as string,
          effortHours: resultActivityContent.effortHours,
          effortMinutes: resultActivityContent.effortMinutes,
        }}
        isActive={true}
      />
    );
  }

  return null;
};
